"use client";

import React from 'react';
import Link from 'next/link';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';
import { Button } from '@/app/components/ui/Button';
import { Check, X, HelpCircle, ArrowRight, ShieldCheck, Banknote, Clock, HeartHandshake, Info, Scale, FileWarning, Heart, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ComparisonPage() {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body">
      <LandingHeader transparent />

      <main className="flex-1">

        {/* HERO SECTION */}
        <section className="relative pt-32 pb-24 px-6 bg-white border-b border-foreground/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="font-display text-5xl md:text-7xl mb-8 tracking-tighter">
              Why choose <span className="text-teal">WillDone?</span>
            </h1>
            <p className="text-xl md:text-2xl font-light text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              We believe we've found the perfect balance. Professional quality without the solicitor price tag.
            </p>
          </div>
        </section>

        {/* COMPARISON TABLE SECTION */}
        <section className="py-24 px-6 bg-white border-b border-foreground/10">
          <div className="container mx-auto max-w-6xl">

            <div className="overflow-x-auto">
              <div className="min-w-[800px]">
                {/* Table Header */}
                <div className="grid grid-cols-5 gap-0 mb-0 items-stretch border-b-2 border-foreground/10">
                  <div className="col-span-1 text-left font-display text-2xl p-6 flex items-end">Feature</div>
                  <div className="col-span-1 p-0 relative min-h-[140px]">
                    <div className="absolute inset-x-0 -top-4 bottom-0 bg-teal text-white flex flex-col items-center justify-center shadow-2xl z-10 border-t-4 border-cta">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cta text-foreground text-xs font-bold px-4 py-1.5 uppercase tracking-widest border border-foreground shadow-sm whitespace-nowrap">
                        Best Value
                      </div>
                      <span className="font-display text-2xl mb-2">WillDone</span>
                      <ShieldCheck className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="col-span-1 p-6 bg-white border-r border-foreground/10 flex flex-col items-center justify-center min-h-[140px]">
                    <span className="font-display text-xl mb-2">Solicitors</span>
                    <Banknote className="w-6 h-6 text-muted-foreground/50" />
                  </div>
                  <div className="col-span-1 p-6 bg-white border-r border-foreground/10 flex flex-col items-center justify-center min-h-[140px]">
                    <span className="font-display text-xl mb-2">DIY Kits</span>
                    <HelpCircle className="w-6 h-6 text-muted-foreground/50" />
                  </div>
                  <div className="col-span-1 p-6 bg-white border-r border-foreground/10 flex flex-col items-center justify-center min-h-[140px]">
                    <span className="font-display text-xl mb-2">Charity Sites</span>
                    <HeartHandshake className="w-6 h-6 text-muted-foreground/50" />
                  </div>
                </div>

                {/* Table Rows */}
                <div className="border border-t-0 border-foreground/10 bg-white">
                  {/* Cost */}
                  <div className="grid grid-cols-5 gap-0 items-stretch border-b border-foreground/10 hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Typical Cost</div>
                    <div className="p-6 flex items-center justify-center text-center font-bold text-teal text-2xl bg-teal/5">£70</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">£200 - £500+</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">£10 - £30</div>
                    <div className="p-6 flex flex-col items-center justify-center text-center text-muted-foreground border-r border-foreground/10 relative">
                      <span className="font-medium">"Free"</span>
                      <span className="text-[10px] text-destructive uppercase tracking-wider font-bold mt-1">Conditions Apply</span>
                    </div>
                  </div>

                  {/* Simplicity */}
                  <div className="grid grid-cols-5 gap-0 items-stretch border-b border-foreground/10 hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Simplicity</div>
                    <div className="p-6 flex items-center justify-center text-center font-bold text-teal bg-teal/5">Very Simple</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Complex</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Difficult</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Variable</div>
                  </div>

                  {/* Expert Review */}
                  <div className="grid grid-cols-5 gap-0 items-stretch border-b border-foreground/10 hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Expert Review</div>
                    <div className="p-6 flex items-center justify-center bg-teal/5"><Check className="text-teal w-8 h-8" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><Check className="text-teal w-6 h-6" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><X className="text-destructive w-6 h-6" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><Check className="text-teal w-6 h-6" /></div>
                  </div>

                  {/* Hidden Costs/Upsells */}
                  <div className="grid grid-cols-5 gap-0 items-stretch border-b border-foreground/10 hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Hidden Costs</div>
                    <div className="p-6 flex items-center justify-center text-center text-teal font-bold bg-teal/5">None</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Hourly rates</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">None</div>
                    <div className="p-6 flex flex-col items-center justify-center text-center border-r border-foreground/10">
                      <span className="text-destructive font-bold">Donation Pressure</span>
                    </div>
                  </div>

                  {/* Convenience */}
                  <div className="grid grid-cols-5 gap-0 items-stretch border-b border-foreground/10 hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Time to Complete</div>
                    <div className="p-6 flex items-center justify-center text-center font-bold text-teal bg-teal/5">15 mins</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Weeks</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">Hours</div>
                    <div className="p-6 flex items-center justify-center text-center text-muted-foreground border-r border-foreground/10">30 mins</div>
                  </div>

                  {/* Legal Validity */}
                  <div className="grid grid-cols-5 gap-0 items-stretch hover:bg-muted/30 transition-colors group">
                    <div className="p-6 font-bold text-lg flex items-center border-r border-foreground/10 text-foreground/80 group-hover:text-foreground">Legally Binding</div>
                    <div className="p-6 flex items-center justify-center bg-teal/5"><Check className="text-teal w-8 h-8" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><Check className="text-teal w-6 h-6" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><HelpCircle className="text-orange-400 w-6 h-6" /></div>
                    <div className="p-6 flex items-center justify-center border-r border-foreground/10"><Check className="text-teal w-6 h-6" /></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* DETAILED BREAKDOWN SECTIONS */}
        <section className="py-24 px-6 bg-background">
          <div className="container mx-auto max-w-5xl space-y-32">

            {/* VS SOLICITORS */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 order-2 md:order-1">
                <div className="space-y-4">
                  <div className="inline-flex items-center justify-center p-3 border-2 border-foreground/10 bg-muted/20 text-foreground mb-4">
                    <Scale className="w-8 h-8" />
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl">Vs. High Street Solicitors</h2>
                  <p className="text-xl text-muted-foreground font-light leading-relaxed">
                    Solicitors offer great service, but for most people, it's overkill. You pay for their office, their time, and their qualifications—often hundreds of pounds for a simple document.
                  </p>
                </div>

                <div className="bg-destructive/5 border-l-4 border-destructive p-6">
                  <h4 className="font-bold text-destructive mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4" /> The Downside
                  </h4>
                  <p className="text-foreground/80 font-medium">Expensive, requires appointments, and can take weeks to finalize.</p>
                </div>
              </div>

              <div className="order-1 md:order-2">
                <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-10 transition-all duration-300 relative group">
                  <div className="absolute -top-3 -right-3 bg-teal text-white px-4 py-1 text-sm font-bold shadow-sm uppercase tracking-wider transform group-hover:scale-105 transition-transform rounded-full">
                    The Smart Choice
                  </div>
                  <h3 className="font-display text-3xl mb-8 text-teal">Why WillDone is better</h3>
                  <ul className="space-y-6">
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Same legal validity for 90% less cost.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">No appointments—do it from your sofa.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Fixed fee. No hourly rates.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* VS DIY */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-1 md:order-1">
                <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-10 transition-all duration-300 relative group">
                  <div className="absolute -top-3 -left-3 bg-teal text-white px-4 py-1 text-sm font-bold shadow-sm uppercase tracking-wider transform group-hover:scale-105 transition-transform rounded-full">
                    Peace of Mind
                  </div>
                  <h3 className="font-display text-3xl mb-8 text-teal">Why WillDone is better</h3>
                  <ul className="space-y-6">
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Every will is reviewed by a specialist.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Smart software prevents common errors.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Clean, printed document (not handwriting).</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-8 order-2 md:order-2">
                <div className="space-y-4">
                  <div className="inline-flex items-center justify-center p-3 border-2 border-foreground/10 bg-muted/20 text-foreground mb-4">
                    <FileWarning className="w-8 h-8" />
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl">Vs. DIY Kits</h2>
                  <p className="text-xl text-muted-foreground font-light leading-relaxed">
                    Paper forms from the post office are cheap, but dangerous. There's no one to check your work. A simple spelling mistake or vague sentence could invalidate your entire will.
                  </p>
                </div>
                <div className="bg-destructive/5 border-l-4 border-destructive p-6">
                  <h4 className="font-bold text-destructive mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4" /> The Downside
                  </h4>
                  <p className="text-foreground/80 font-medium">High risk of errors, no professional oversight, easy to challenge legally.</p>
                </div>
              </div>
            </div>

            {/* VS CHARITY */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-8 order-2 md:order-1">
                <div className="space-y-4">
                  <div className="inline-flex items-center justify-center p-3 border-2 border-foreground/10 bg-muted/20 text-foreground mb-4">
                    <Heart className="w-8 h-8" />
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl">Vs. Charity "Free" Wills</h2>
                  <p className="text-xl text-muted-foreground font-light leading-relaxed">
                    Many charities offer "free" will writing services. While noble, the goal is to get you to leave a legacy. You may feel pressured to donate a significant portion of your estate to pay for the service.
                  </p>
                </div>
                <div className="bg-destructive/5 border-l-4 border-destructive p-6">
                  <h4 className="font-bold text-destructive mb-2 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <AlertTriangle className="w-4 h-4" /> The Downside
                  </h4>
                  <p className="text-foreground/80 font-medium">Emotional pressure to donate, your family might inherit less, your data is often used for marketing.</p>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="bg-white rounded-2xl border border-foreground/5 shadow-[0_0_50px_-12px_rgba(53,140,127,0.15)] p-8 md:p-10 transition-all duration-300 relative group">
                  <div className="absolute -top-3 -right-3 bg-teal text-white px-4 py-1 text-sm font-bold shadow-sm uppercase tracking-wider transform group-hover:scale-105 transition-transform rounded-full">
                    Total Freedom
                  </div>
                  <h3 className="font-display text-3xl mb-8 text-teal">Why WillDone is better</h3>
                  <ul className="space-y-6">
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">Total freedom. Leave money to who you want.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">No guilt trips or sales pressure.</span>
                    </li>
                    <li className="flex items-start gap-4 group/item">
                      <div className="mt-1 bg-teal/10 text-teal p-1 rounded-sm group-hover/item:bg-teal group-hover/item:text-white transition-colors">
                        <Check className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium text-foreground/90">We don't sell your data.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
}

